/** Экран подтверждения: договор подписан (п. 3.10 ТЗ). */

import { useState } from 'react'

import { ApiError, api, openContract } from '../api/client'
import type { Application } from '../api/types'
import { Alert, Button, StatusBadge } from '../components/ui'
import { consentLabel, formatDate, formatDateTime, fullName } from '../lib/format'

interface Props {
  application: Application
  onNewApplication: () => void
  onToList: () => void
}

export function DoneScreen({ application, onNewApplication, onToList }: Props) {
  const hasSignedPdf = application.files.some((file) => file.kind === 'signed_pdf')
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState<string | null>(null)

  // Отправка данных в карточку договора. Отдельным действием, а не следом за
  // подписанием: это запись в боевой биллинг, и момент выбирает человек.
  const [pushing, setPushing] = useState(false)
  const [pushed, setPushed] = useState(false)
  const [pushError, setPushError] = useState<string | null>(null)

  // Достаточно логина: по нему находится договор, а писать всегда есть что —
  // как минимум ФИО и паспорт.
  const canPush = Boolean(application.login)

  const pushToBilling = async () => {
    setPushing(true)
    setPushError(null)
    try {
      await api.pushToBilling(application.id)
      setPushed(true)
    } catch (err) {
      setPushError(
        err instanceof ApiError ? err.message : 'Не удалось записать данные в биллинг',
      )
    } finally {
      setPushing(false)
    }
  }

  const open = async () => {
    setOpening(true)
    setOpenError(null)
    try {
      await openContract(application.id)
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : 'Не удалось открыть договор')
    } finally {
      setOpening(false)
    }
  }

  return (
    <div className="card">
      <h2>Договор подписан</h2>
      <p className="subtitle">
        Данные заявки сохранены и переданы в очередь на выгрузку в биллинг.
      </p>

      <Alert kind="ok" title={`Договор № ${application.contract_number ?? '—'}`}>
        Подписан {formatDateTime(application.signed_at)}.
      </Alert>

      {!hasSignedPdf && (
        <Alert kind="warn" title="Подписанный файл ещё не получен">
          Подпись зафиксирована, но PDF от сервиса подписания пока не скачался. Он
          подтянется автоматически — заявка перейдёт в статус «Готов к выгрузке».
        </Alert>
      )}

      <dl className="summary">
        <dt>Статус</dt>
        <dd>
          <StatusBadge status={application.status} />
        </dd>

        <dt>Абонент</dt>
        <dd>{fullName(application) || '—'}</dd>

        <dt>Логин абонента</dt>
        <dd>{application.login ?? '—'}</dd>

        <dt>Дата договора</dt>
        <dd>{formatDate(application.contract_date)}</dd>

        <dt>Адрес подключения</dt>
        <dd>{application.connection_address ?? '—'}</dd>

        <dt>Тариф</dt>
        <dd>
          {application.tariff_name ?? '—'}
          {application.tariff_price ? `, ${application.tariff_price}` : ''}
        </dd>

        {application.tv_package_name && (
          <>
            <dt>ТВ-пакет</dt>
            <dd>
              {application.tv_package_name}
              {application.tv_package_price ? `, ${application.tv_package_price}` : ''}
            </dd>
          </>
        )}

        <dt>Телефон</dt>
        <dd>{application.phone ?? '—'}</dd>

        <dt>Электронная почта</dt>
        <dd>{application.email ?? '—'}</dd>

        {application.consents.map((consent) => (
          <div key={consent.kind} style={{ display: 'contents' }}>
            <dt>{consentLabel(consent.kind)}</dt>
            <dd>{formatDateTime(consent.accepted_at)}</dd>
          </div>
        ))}
      </dl>

      {openError && (
        <Alert kind="error" title="Договор не открылся" onDismiss={() => setOpenError(null)}>
          {openError}
        </Alert>
      )}

      {pushed && (
        <Alert kind="ok" title="Данные записаны в биллинг">
          Карточка договора логина {application.login} заполнена: ФИО, паспорт, телефон,
          почта, дата рождения и адреса. Тарифы не отправляются — услуги заводит
          оператор. Повторная отправка просто перезапишет данные.
        </Alert>
      )}

      {pushError && (
        <Alert
          kind="error"
          title="Данные не записаны"
          onRetry={pushToBilling}
          onDismiss={() => setPushError(null)}
        >
          {pushError}
        </Alert>
      )}

      <div className="actions" style={{ marginTop: 20 }}>
        <Button variant="secondary" onClick={onToList}>
          К списку заявок
        </Button>
        <Button variant="secondary" onClick={open} busy={opening}>
          Открыть договор
        </Button>
        {canPush && (
          <Button variant="secondary" onClick={pushToBilling} busy={pushing} disabled={pushed}>
            {pushed ? 'Данные в биллинге' : 'Записать данные в биллинг'}
          </Button>
        )}
        <Button onClick={onNewApplication}>Новое подключение</Button>
      </div>
    </div>
  )
}
